import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { SynapticText } from '@/components/hero/SynapticText';

interface JourneyCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  number: string;
  index: number;
  shouldStartTyping: boolean;
  onComplete: () => void;
}

export const JourneyCard = ({ 
  icon: Icon, 
  title, 
  description, 
  number, 
  index,
  shouldStartTyping,
  onComplete
}: JourneyCardProps) => {
  const [showTyping, setShowTyping] = useState(false);
  const [typingComplete, setTypingComplete] = useState(false);

  useEffect(() => {
    if (shouldStartTyping && !showTyping) {
      setShowTyping(true);
    }
  }, [shouldStartTyping, showTyping]);

  const handleTypingComplete = () => {
    setTypingComplete(true);
    setTimeout(() => {
      onComplete();
    }, 500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50, scale: 0.8 }}
      whileInView={{ 
        opacity: 1, 
        x: 0, 
        scale: 1,
        transition: { 
          duration: 0.6,
          delay: index * 0.1,
          ease: [0.25, 0.46, 0.45, 0.94]
        }
      }}
      viewport={{ once: true }}
      className="group relative h-full"
    >
      {/* Gentle glow effect */}
      <motion.div 
        className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/10 opacity-0"
        animate={showTyping ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      />
      
      {/* Main card - Responsive padding and sizing */}
      <div className="relative bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-lg border border-white/30 rounded-2xl p-4 sm:p-6 h-full overflow-hidden transition-all duration-500 shadow-xl hover:from-primary/30 hover:to-accent/30 min-h-[280px] sm:min-h-[320px]">
        
        {/* Subtle background gradient */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-background/5 to-primary/5"
          animate={showTyping ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        />

        <div className="relative z-10">
          {/* Number indicator - Responsive sizing */}
          <motion.div 
            className={`text-2xl sm:text-3xl lg:text-4xl font-bold font-mono mb-3 sm:mb-4 transition-colors duration-500 ${
              showTyping ? 'text-primary' : 'text-primary/40'
            }`}
            animate={showTyping ? { scale: 1.1 } : { scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            {number}
          </motion.div>
          
          {/* Icon - Responsive sizing */}
          <motion.div 
            className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 backdrop-blur-sm border rounded-xl mb-3 sm:mb-4 transition-all duration-500 ${
              showTyping 
                ? 'bg-white/20 border-white/30' 
                : 'bg-white/10 border-white/20'
            }`}
            animate={showTyping ? { scale: 1.05 } : { scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Icon className={`w-6 h-6 sm:w-7 sm:h-7 transition-colors duration-500 ${showTyping ? 'text-white' : 'text-white/80'}`} />
          </motion.div>
          
          {/* Title - Responsive typography and better mobile spacing */}
          <div className="mb-2 sm:mb-3">
            {showTyping ? (
              <SynapticText 
                text={title}
                delay={200}
                className="text-base sm:text-lg font-bold text-white leading-tight"
                immediate={false}
              />
            ) : (
              <h3 className="text-base sm:text-lg font-bold text-white/80 leading-tight">
                {title}
              </h3>
            )}
          </div>
          
          {/* Description - Better mobile typography and spacing */}
          <div className="min-h-[4rem] sm:min-h-[3rem]">
            {showTyping ? (
              <SynapticText 
                text={description}
                delay={1000 + title.length * 50}
                className="text-sm text-white/90 leading-relaxed"
                immediate={false}
                onComplete={handleTypingComplete}
              />
            ) : (
              <p className="text-sm text-white/80 leading-relaxed">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Connection line to next card - Hidden on mobile and tablet */}
        {index < 4 && (
          <motion.div 
            className="hidden xl:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-20"
            initial={{ opacity: 0, scaleX: 0 }}
            animate={typingComplete ? { opacity: 1, scaleX: 1 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <svg width="48" height="4" viewBox="0 0 48 4" className="text-primary/60">
              <motion.path
                d="M0,2 L48,2"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={typingComplete ? { pathLength: 1 } : {}}
                transition={{ duration: 1, ease: "easeInOut" }}
              />
              <motion.circle
                cx="48"
                cy="2"
                r="2"
                fill="currentColor"
                initial={{ scale: 0 }}
                animate={typingComplete ? { scale: 1 } : {}}
                transition={{ duration: 0.3, delay: 0.8 }}
              />
            </svg>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};